import React, { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Tabs, Tab, Button, Stack, Chip, Alert,
  CircularProgress, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PublishIcon from '@mui/icons-material/Publish';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ArticleIcon from '@mui/icons-material/Article';
import {
  useForm16Detail, useRefreshForm16, usePublishForm16, useRevokeForm16,
  form16PdfUrl,
} from '../../../hooks/useForm16';
import Form16PartA from './Form16PartA';
import Form16PartB from './Form16PartB';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const btnSx = { textTransform: 'none', fontWeight: 700, borderRadius: '12px' };

export default function Form16Detail() {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useForm16Detail(id);
  const refresh = useRefreshForm16();
  const publish = usePublishForm16();
  const revoke = useRevokeForm16();
  const [tab, setTab] = useState(0);
  const [publishError, setPublishError] = useState(null);

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error || !data) return <Box sx={{ p: { xs: 2, md: 4 } }}><Alert severity="error" sx={{ borderRadius: '14px' }}>Failed to load Form 16.</Alert></Box>;

  const published = data.status === 'PUBLISHED';

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* ---- Header card ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '16px', bgcolor: `${ACCENT}14`, color: ACCENT, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ArticleIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.6rem' }, color: '#111827', letterSpacing: '-0.02em', lineHeight: 1.15 }}>
                Form 16 — {data.employee_name} ({data.employee_no})
              </Typography>
              <Typography variant="caption" sx={{ color: '#6b7280' }}>
                FY {data.fy_label} · Period {data.period_from} → {data.period_to} · Revision R{data.revision_no}
                {data.certificate_no ? ` · Certificate No.: ${data.certificate_no}` : ''}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={data.status}
              sx={{ fontWeight: 700, bgcolor: published ? '#f0fdf4' : '#f3f4f6', color: published ? '#16a34a' : '#6b7280' }}
            />
            <Button component={RouterLink} to="/payroll/form16" size="small" variant="outlined" sx={{ ...btnSx, color: '#6b7280', borderColor: '#e5e7eb' }}>Back to list</Button>
          </Stack>
        </Stack>
      </Paper>

      {/* ---- Actions ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Button startIcon={<RefreshIcon />} disabled={refresh.isPending || data.status === 'PUBLISHED'}
            onClick={() => refresh.mutate(data.id, { onSuccess: () => refetch() })}
            sx={{ ...btnSx, color: ACCENT }}>
            Refresh from payroll
          </Button>
          <Button startIcon={<PublishIcon />} variant="contained" disableElevation
            disabled={publish.isPending || data.status === 'PUBLISHED'}
            onClick={() => {
              setPublishError(null);
              publish.mutate(data.id, {
                onSuccess: () => refetch(),
                onError: (err) => setPublishError(err?.response?.data?.error || err.message),
              });
            }}
            sx={{ ...btnSx, background: 'linear-gradient(135deg,#3b82f6,#2f6bff)', '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' } }}>
            Publish
          </Button>
          <Button startIcon={<RemoveCircleOutlineIcon />} color="error"
            disabled={revoke.isPending || data.status !== 'PUBLISHED'}
            onClick={() => revoke.mutate(data.id, { onSuccess: () => refetch() })}
            sx={btnSx}>
            Revoke
          </Button>
          <Divider flexItem orientation="vertical" sx={{ borderColor: '#eef0f4' }} />
          <Button startIcon={<DownloadIcon />} component="a"
            href={form16PdfUrl(data.id, 'part-a')} target="_blank" rel="noreferrer"
            sx={{ ...btnSx, color: ACCENT }}>
            Part A PDF
          </Button>
          <Button startIcon={<DownloadIcon />} component="a"
            href={form16PdfUrl(data.id, 'part-b')} target="_blank" rel="noreferrer"
            sx={{ ...btnSx, color: ACCENT }}>
            Part B PDF
          </Button>
          <Button startIcon={<DownloadIcon />} variant="contained" disableElevation component="a"
            href={form16PdfUrl(data.id, 'combined')} target="_blank" rel="noreferrer"
            sx={{ ...btnSx, background: 'linear-gradient(135deg,#3b82f6,#2f6bff)', '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' } }}>
            Combined PDF
          </Button>
        </Stack>
        {publishError && <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>{publishError}</Alert>}
      </Paper>

      {/* ---- Part A / Part B tabs ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, color: '#6b7280' },
            '& .Mui-selected': { color: `${ACCENT} !important` },
            '& .MuiTabs-indicator': { backgroundColor: ACCENT, height: 3, borderRadius: 3 },
          }}
        >
          <Tab label="Part A — TDS Summary" />
          <Tab label="Part B — Salary & Tax Computation" />
        </Tabs>
        <Divider sx={{ borderColor: '#eef0f4' }} />
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {tab === 0 && <Form16PartA form16={data} onChanged={refetch} />}
          {tab === 1 && (
            <Form16PartB
              form16={data}
              editable={data.status !== 'PUBLISHED'}
              onSaved={refetch}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}
